/**
 * 引っ張って更新 (2026-09-21「上に出る知らせ」G 案で見た目を作り直した)。
 *
 * X と同じ作法: 一番上で下へ引くと **中身が指に付いて下がり、空いた所 (タブの下) に輪** が出る。
 * 輪の弧は引いた分だけ伸び、しきい値を越えると紫になって小さく弾む。文字は出さない。
 * 離すと中身は 56px 下がったまま輪が回り、終わったら中身が戻って、タブの下に
 * 高さ 44px の静かなカプセルで結果を言う (topnotice.ts の showResult):
 *   「3 件の新しいポスト」「新しいポストはありません」「更新できませんでした [再試行]」
 * ホーム以外 (検索など) は丸ごと読み直し、戻ってきたら「更新しました」。
 *
 * 旧実装はタブの上にグラデのピル「✨ 離して更新」を重ね、結果もトーストと同じ場所
 * (上から 64px 固定) にぴったり重なり、失敗にも ✓ の印が付いていた。
 *
 * /api/v1/timeline/refresh は {count, html, latest_id} を返す (Go handler 側で対応済)。
 * count > 0 のとき html を timeline 先頭に insertBefore で差し込む。
 *
 * localStorage `morioka-ptr-mode`: off なら起動しない (/settings/display の「無効化」)。
 */

import { u } from "./url";
import { landNewPosts } from "./new-posts-motion";
import { apiFetch, type TimelineRefreshResp } from "./api";
import { topLayer, follow, measure, showResult, type ResultKind } from "./topnotice";

const PULL_THRESHOLD = 70;
const MAX_PULL = 110;
/** 読み込み中に中身を止めておく高さ (輪 28px + 上下の余白) */
const HOLD = 56;

function effectivePtrMode(): "on" | "off" {
  let raw = "auto";
  try {
    raw = localStorage.getItem("morioka-ptr-mode") || "auto";
  } catch (_) { /* ignore */ }
  return raw === "off" ? "off" : "on";
}

export function initPullRefresh(): void {
  if (effectivePtrMode() === "off") return;
  // タッチ非対応端末 (= 通常 PC) では何もしない。PHP 版と同じ挙動。
  if (!("ontouchstart" in window) && !(navigator.maxTouchPoints > 0)) return;

  let startY = 0;
  let startX = 0;
  let pullDist = 0;
  let pulling = false;
  let armed = false;
  let lastReady = false;
  let loading = false;

  /** 引いた分だけ下げる中身 = タブ (.col-main-header) より後ろの兄弟。タブが無ければ全部。 */
  function shiftTargets(): HTMLElement[] {
    const col = document.querySelector<HTMLElement>(".col-main");
    if (!col) return [];
    const kids = Array.from(col.children) as HTMLElement[];
    const hi = kids.findIndex((k) => k.classList.contains("col-main-header"));
    return kids.slice(hi + 1).filter((k) => k.id !== "mpTopNotice");
  }
  /** 中身を下げる。⚠ 戻したら transform を必ず空にする (fixed の子の包含ブロックが変わる)。 */
  function setShift(px: number, animate: boolean): void {
    shiftTargets().forEach((el) => {
      el.style.transition = animate ? "transform .36s cubic-bezier(.2,.9,.25,1)" : "none";
      el.style.transform = px > 0 ? "translateY(" + px + "px)" : "";
    });
    if (animate && px === 0) {
      window.setTimeout(() => {
        if (pulling || loading) return;
        shiftTargets().forEach((el) => { el.style.transition = ""; el.style.transform = ""; });
      }, 400);
    }
  }
  function paint(dist: number): void {
    const L = topLayer();
    const pp = Math.min(1, dist / PULL_THRESHOLD);
    L.style.setProperty("--pp", pp.toFixed(3));
    L.style.setProperty("--gap", dist + "px");
    const arc = L.querySelector<SVGElement>(".tn-ring-arc");
    if (arc) arc.style.strokeDasharray = (pp * 88).toFixed(1) + " 100";
  }

  function hasInnerScrollAncestor(target: EventTarget | null): boolean {
    let node = target as Node | null;
    while (node && node !== document.body && node !== document.documentElement && node.nodeType === 1) {
      const el = node as Element;
      const cs = window.getComputedStyle ? window.getComputedStyle(el) : null;
      const oy = cs ? cs.overflowY : "";
      if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight + 1) {
        return true;
      }
      node = el.parentElement;
    }
    return false;
  }

  function isEligible(touchTarget: EventTarget | null): boolean {
    if (document.body.classList.contains("lightbox-open")) return false;
    // みんなのいまティッカー起点のタッチは PtR を arm しない — 横スワイプ
    // (先送り/巻き戻し) のわずかな下ブレ (dy>=4) で「離して更新」が誤発火する。
    // 帯の外のタッチは従来どおり。
    {
      const n = touchTarget as Node | null;
      const el = n && n.nodeType === 1 ? (n as Element) : n ? n.parentElement : null;
      if (el && el.closest && el.closest(".mn-ticker, .ps-rail")) return false;
    }
    // オーバーレイ UI (ボトムシート / ドロワー / モーダル) の中で始まったタッチは
    // PtR を arm しない。シートを下へ引いて閉じる操作 (xstyle/sheet-drag.ts) が
    // そのまま「離して更新」になり、ホーム以外では fullReload まで走ってしまう
    // (2026-08-26 に実測。裏のページはスクロール禁止なので scrollY===0 が常に成立し、
    //  条件をすり抜ける)。
    if (
      document.body.classList.contains("mp-sheet-open") ||
      document.body.classList.contains("drawer-open") ||
      document.body.classList.contains("account-sheet-open") ||
      // おかえりモーダル (2026-09-08)。背景はスクロールしないので scrollY が 0 のまま =
      // シートの中を下へ引くとそのまま「離して更新」になり、書きかけの答えが消える。
      document.body.classList.contains("okaeri-open")
    ) {
      return false;
    }
    {
      const n = touchTarget as Node | null;
      const el = n && n.nodeType === 1 ? (n as Element) : n ? n.parentElement : null;
      if (el && el.closest && el.closest(".mp-sheet-host, .account-sheet, .side-drawer, .drawer-overlay, .composer-modal, .modal-overlay, .report-modal, .ok-root, dialog")) {
        return false;
      }
    }
    const ae = document.activeElement as HTMLElement | null;
    if (ae && ae.matches && ae.matches('input, textarea, select, [contenteditable="true"]')) return false;
    const sy = window.scrollY || document.documentElement.scrollTop || 0;
    if (sy > 0) return false;
    if (touchTarget && hasInnerScrollAncestor(touchTarget)) return false;
    return true;
  }

  function vibrate(ms: number): void {
    if (typeof navigator.vibrate === "function") {
      try { navigator.vibrate(ms); } catch (_) { /* ignore */ }
    }
  }

  function setIndicatorOffset(dist: number): void {
    const L = topLayer();
    const isReady = dist >= PULL_THRESHOLD;
    L.setAttribute("data-ptr", isReady ? "armed" : "pull");
    paint(dist);
    setShift(dist, false);
    if (isReady && !lastReady) {
      // 越えた瞬間の「カチッ」(輪が小さく弾む)。keyframes を掛け直す
      const ptr = L.querySelector<HTMLElement>(".tn-ptr");
      if (ptr) { ptr.classList.remove("is-click"); void ptr.offsetWidth; ptr.classList.add("is-click"); }
      vibrate(12);
    }
    lastReady = isReady;
  }

  function snapBack(): void {
    const L = topLayer();
    L.setAttribute("data-ptr", "idle");
    paint(0);
    setShift(0, true);
    lastReady = false;
    follow(false);
  }


  function isHome(): boolean {
    const path = (window.location.pathname || "/").replace(/\/+$/, "/");
    const prefix = window.V2_URL_PREFIX || "";
    if (path === "/" || /\/index\.php$/.test(path)) return true;
    if (prefix !== "" && path === prefix + "/") return true;
    return false;
  }

  function fullReload(): void {
    try { sessionStorage.setItem("mp_pulled_refresh", String(Date.now())); } catch (_) { /* ignore */ }
    window.location.reload();
  }

  // findFirstPost は「ピン留めではない、表示中の最も新しい投稿」を返す。
  // - since_id 計算: ピン留めはタイムライン上では古い ID を持つことがあり、それを
  //   基準にすると既存の表示済み投稿が全部「新着」扱いで重複挿入されてしまう
  //   (= 「30件の新しい投稿」と誤表示 + ピン留めが画面下へ押し出されて消える)。
  // - insertBefore の基準: 新着をピン留めの上に積まないことで、ピン留めを常に
  //   最上段に固定したまま新着を間に挟める。
  // 非ピン投稿が DOM 上に 1 件も無い (= ピン留め単独表示) ときは null を返す。
  function findFirstPost(): HTMLElement | null {
    const all = document.querySelectorAll<HTMLElement>(
      ".col-main .post[data-post-id], main .post[data-post-id], .post[data-post-id]"
    );
    for (let i = 0; i < all.length; i++) {
      const p = all[i];
      if (!p.querySelector(".post-pinned-label")) return p;
    }
    return null;
  }

  // 既に DOM 上にある投稿 ID を集めて、重複挿入を防ぐ
  function collectExistingPostIds(): Set<string> {
    const set = new Set<string>();
    document.querySelectorAll<HTMLElement>(".post[data-post-id]").forEach((el) => {
      const id = el.getAttribute("data-post-id");
      if (id) set.add(id);
    });
    return set;
  }

  function ajaxRefreshHome(): Promise<TimelineRefreshResp> {
    // since_id は表示中の (非ピン) 投稿の最大 ID。findFirstPost が non-pinned かつ
    // タイムライン先頭にあるので普通はその ID が最大だが、念のため全 post を走査して
    // max を取る (ピン留めだけは除外)。これで「ピンの古い ID を since_id にして全部
    // 重複返ってくる」事故を確実に防ぐ。
    let sinceId = 0;
    document.querySelectorAll<HTMLElement>(".post[data-post-id]").forEach((el) => {
      if (el.querySelector(".post-pinned-label")) return;
      const n = parseInt(el.getAttribute("data-post-id") || "0", 10);
      if (n > sinceId) sinceId = n;
    });
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab") || "foryou";
    const path = "/api/v1/timeline/refresh?since_id=" + sinceId + "&tab=" + encodeURIComponent(tab);
    return apiFetch<TimelineRefreshResp>(path);
  }

  function triggerRefresh(): void {
    vibrate(14);
    const L = topLayer();
    loading = true;
    L.setAttribute("data-ptr", "loading");
    paint(PULL_THRESHOLD);
    setShift(HOLD, true);
    if (isHome()) {
      let settled = false;
      // 安全網: 10 秒たっても返ってこなければ失敗として閉じる
      const guard = window.setTimeout(() => { if (!settled) { settled = true; finish("fail", 0); } }, 10000);
      ajaxRefreshHome()
        .then((data) => { if (settled) return; settled = true; clearTimeout(guard); handleAjaxDone(data); })
        .catch(() => { if (settled) return; settled = true; clearTimeout(guard); finish("fail", 0); });
    } else {
      // 丸ごと読み直す。輪は回したまま (読み直しそのものが「更新中」の合図)
      fullReload();
    }
  }

  /** 中身を戻して、タブの下に結果を出す */
  function finish(kind: ResultKind, count: number): void {
    loading = false;
    const L = topLayer();
    L.setAttribute("data-ptr", "idle");
    paint(0);
    setShift(0, true);
    follow(false);
    showResult(kind, count, kind === "fail" ? retry : undefined);
  }
  /** 「再試行」: 引っ張らずに同じ更新をもう一度 */
  function retry(): void {
    if (loading) return;
    follow(true);
    measure();
    triggerRefresh();
  }

  function handleAjaxDone(data: TimelineRefreshResp): void {
    let displayCount = data.count || 0;
    if (displayCount > 0 && data.html) {
      const firstPost = findFirstPost();
      const parent = firstPost ? firstPost.parentNode : null;
      if (firstPost && parent) {
        const tmp = document.createElement("div");
        tmp.innerHTML = data.html;
        // サーバは新しい順 (descending) で返す。各要素を firstPost (= 非ピン留め
        // の先頭) の直前に insertBefore すると DOM 順は「挿入順, ..., firstPost」
        // になるので、そのまま forEach すれば最終 DOM は [新, ..., 旧, 既存先頭]。
        // ピン留めの上には積まれないので、ピン留めは最上段に残り続ける。
        // 既存 DOM と重複する post_id は弾く (安全網: since_id 計算が外れても
        // 「30件の新しい投稿」と誤表示しない)。
        const existing = collectExistingPostIds();
        const inserts = Array.from(tmp.children).filter((node) => {
          const id = (node as HTMLElement).getAttribute?.("data-post-id");
          if (id && existing.has(id)) return false;
          return true;
        });
        displayCount = inserts.length;
        if (displayCount > 0) {
          // 新着ピルと同じ動き (new-posts-motion.ts): 位置を保って差し込み、先頭へ滑り、
          // 新しい投稿が上から順に現れる。
          landNewPosts(parent, firstPost, inserts, 0);
          try { window.dispatchEvent(new Event("timeline-prepended")); } catch (_) { /* ignore */ }
        }
      } else {
        fullReload();
        return;
      }
    }
    finish(displayCount > 0 ? "new" : "none", displayCount);
  }


  document.addEventListener("touchstart", (e) => {
    if (e.touches.length !== 1) { armed = false; return; }
    if (loading || !isEligible(e.target)) { armed = false; return; }
    armed = true;
    pulling = false;
    pullDist = 0;
    startY = e.touches[0].clientY;
    startX = e.touches[0].clientX;
  }, { passive: true });

  // ⚠ 非 passive の touchmove を document に張りっぱなしにしない (2026-09-23)。
  // 非 passive の touchmove が 1 つでもあると、ブラウザは指が動くたびに JS の返事を
  // 待ってからスクロールを進める (= メインスレッドが忙しい瞬間にスクロールが引っかかる)。
  // 引っ張って更新が要るのはページの先頭に居るときだけなので、非 passive のリスナは
  // scrollY が 0 の間だけ張り、離れたら外す (gate)。それ以外の場所では、スクロールは
  // ブラウザ (コンポジタ) が JS を待たずに進める。
  function onMove(e: TouchEvent): void {
    if (!armed) return;
    const y = e.touches[0].clientY;
    const dy = y - startY;
    // 横優位の動き (xstyle/hswipe.ts のドロワー / タブスワイプ) には引っ張りを始めない。
    // 軸を見ずに dy>=4 で始めると、横スワイプの僅かな下ぶれでインジケータが出て
    // preventDefault まで走り、横の追従と取り合いになる (2026-08-26)
    // slop (12px) を超えて横が優位な時だけ降りる。1 サンプル目の僅かな横ぶれで
    // 降りると、親指の弧を描く自然な引っ張りが効かなくなる。
    const dxNow = Math.abs(e.touches[0].clientX - startX);
    if (!pulling && dxNow > 12 && dxNow > Math.abs(dy)) { armed = false; return; }
    if (dy < 4) return;
    if ((window.scrollY || 0) > 0) {
      armed = false;
      if (pulling) snapBack();
      pulling = false;
      return;
    }
    if (!pulling) {
      pulling = true;
      follow(true);
      measure();
    }
    pullDist = Math.min(MAX_PULL, Math.round(Math.sqrt(dy) * 9));
    setIndicatorOffset(pullDist);
    if (e.cancelable) e.preventDefault();
  }
  let gateOn = false;
  function gate(): void {
    const atTop = (window.scrollY || 0) <= 0;
    if (atTop === gateOn) return;
    gateOn = atTop;
    if (atTop) document.addEventListener("touchmove", onMove, { passive: false });
    else document.removeEventListener("touchmove", onMove);
    // 診断用の印 (DevTools で「いま非 passive のリスナがあるか」が分かる)
    document.documentElement.setAttribute("data-ptr-gate", atTop ? "1" : "0");
  }
  gate();
  window.addEventListener("scroll", gate, { passive: true });
  window.addEventListener("pageshow", gate);
  document.addEventListener("mp:navigated", gate);

  document.addEventListener("touchend", () => {
    if (!pulling) { armed = false; return; }
    armed = false;
    pulling = false;
    if (pullDist >= PULL_THRESHOLD) triggerRefresh();
    else snapBack();
    pullDist = 0;
  }, { passive: true });

  document.addEventListener("touchcancel", () => {
    if (pulling) snapBack();
    armed = false;
    pulling = false;
    pullDist = 0;
  }, { passive: true });

  // ===== コールドスタート対策 (warm-up) =====
  // ホーム表示直後に /api/v1/timeline/refresh をバックグラウンドで叩いておき、
  // 初回 PtR の体感速度を上げる。
  function warmTimelineRefresh(): void {
    if (!isHome() || !window.fetch) return;
    const firstPost = findFirstPost();
    if (!firstPost) return;
    const sinceId = parseInt(firstPost.getAttribute("data-post-id") || "0", 10);
    if (sinceId <= 0) return;
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab") || "foryou";
    const url = u("/api/v1/timeline/refresh") + "?since_id=" + sinceId + "&tab=" + encodeURIComponent(tab) + "&warm=1";
    try {
      fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Warm-Request": "1" },
      }).catch(() => { /* ignore */ });
    } catch (_) { /* ignore */ }
  }
  warmTimelineRefresh();

  // ===== 読み直したあと =====
  // fullReload() が sessionStorage に印を残す → 戻ってきたら「更新しました」。
  try {
    const stamp = sessionStorage.getItem("mp_pulled_refresh");
    if (stamp) {
      sessionStorage.removeItem("mp_pulled_refresh");
      const age = Date.now() - parseInt(stamp, 10);
      if (age >= 0 && age < 5000) showResult("reloaded", 0);
    }
  } catch (_) { /* ignore */ }
}
